import React, { useEffect, useMemo, useState } from "react";

import { AxiosError } from "axios";
import moment, { Moment } from "moment/moment";
import { v4 as uuid } from "uuid";

import {
  getEnvironment,
  getUserFacingApiErrorMessage,
  isFeatureActiveOnCurrentFarm,
  isFieldEnrolled,
  useAddFieldPracticeParticipations,
  useBulkUpdateCropDates,
  useBulkUpdateCropSeedingPractice,
  useBulkUpdateCropYield,
  useBulkUpdateMineralFertilizerPractice,
  useBulkUpdateOrganicFertilizerPractice,
  useBulkUpdateTillagePractice,
  useDeleteFieldPracticeParticipations,
  useGetAllFieldPracticesByFarm,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldDetails,
  useGetPracticeCrops,
  useGetVerificationEditingRequests,
  usePracticeManagementCropParameter,
  useTranslation,
  useUpdateFieldCropPractice,
  useUpdateVerificationEditingRequest,
} from "utils";

import { FEATURE_FLAG_SLUGS, HARVEST_UNIT_VALUE, HarvestUnitValue } from "consts";

import {
  BulkUpdateCropYieldRequestBody,
  BulkUpdateMineralFertilizerPracticesRequestBody,
  BulkUpdateOrganicFertilizerPracticesRequestBody,
  BulkUpdateSeedingPracticesRequestBody,
  BulkUpdateTillagePracticesRequestBody,
  CropFCPO,
  EDITING_REQUEST_TYPE,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeFCPO,
  PracticeSlug,
  VerificationEditingRequest,
} from "models";

import { AlertBanner, Button, Drawer, Form, StickyFooterButtons, Message as message } from "components";

import { FormPracticeObject } from "pages/app/all-field-view/components/AddEditCropDrawer.tsx";
import { UndersowingOverlapWarningModal } from "pages/app/all-field-view/components/UndersowingOverlapWarningModal.tsx";
import { CropYieldSection } from "pages/app/all-field-view/components/forms/crop/CropYieldSection.tsx";
import { AddEditOrganicAndMineralFertilizers } from "pages/app/all-field-view/components/forms/fertilizers/AddEditOrganicAndMineralFertilizers.tsx";
import { AddEditTillageForm } from "pages/app/all-field-view/components/forms/tillage/AddEditTillageForm.tsx";
import { CROP } from "pages/app/all-field-view/constants/cropForm.ts";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { createCropPracticeRequestBody } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { getDefaultBulkDataEntryDateValues, getPracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { getUndersowingWarningsForChanges } from "pages/app/all-field-view/utils/CropValidationUtils";
import { getInputRequirementConfig, isRequired } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";
import { MissingCropGrowingPeriod } from "pages/app/historical-practice-data-table-view/components/MissingCropGrowingPeriod";
import { MissingDataDrawerHeader } from "pages/app/historical-practice-data-table-view/components/MissingDataDrawerHeader.tsx";
import { MissingSeedingForm } from "pages/app/historical-practice-data-table-view/components/MissingSeedingForm.tsx";

const VALIDATION_DELAY_MS = 150;

// pseudo practice type: yield is not a real PRACTICE_SLUG, but the all-fields data table view still needs to edit it through this drawer
export const YIELD_PSEUDO_PRACTICE_TYPE = "YIELD";
export type MissingDataDrawerPracticeType = PracticeSlug | typeof YIELD_PSEUDO_PRACTICE_TYPE;

const FakeOrganicFertilizerPracticeBundle: PracticeBundle = {
  active: true,
  hasConfirmedData: false,
  slug: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
  startYear: null,
  discontinuedYear: null,
  reactivatedYear: null,
  enrollable: true,
};

export type MissingDataForm = {
  key: string;
  fieldId: string;
  year: number;
  crop?: CropFCPO;
  // an empty "Add crop" slot rather than an existing crop
  isPlaceholder?: boolean;
  seedings: string[] | null;
  tillages: PracticeFCPO[] | null;
  organicFertilizers: PracticeFCPO[] | null;
  mineralFertilizers?: PracticeFCPO[] | null;
  isMineralEditable?: boolean;
  areMineralDatesEditable?: boolean;
  isCropNameEditable?: boolean;
  isGrowingPeriodEditable: boolean;
  isSeedingEditable: boolean;
  isOrganicEditable: boolean;
  isTillageEditable: boolean;
  isTillageDepthEditable: boolean;
  areTillageDatesEditable: boolean;
  areOrganicDatesEditable: boolean;
  isYieldEditable?: boolean;
  yieldPerHa?: number | null;
  yieldPerHaUnit?: HarvestUnitValue | null;
};
export type AddEditMissingSeedingDrawerProps = {
  showDrawer: boolean;
  onClose: () => void;
  missingDataFormItems: MissingDataForm[];
  practiceType: MissingDataDrawerPracticeType;
  selectedYear: number;
  shouldValidateOnMount?: boolean;
  onDatesCollected?: (key: string, seedingStart: string, seedingEnd: string, harvestStart: string, harvestEnd: string) => void;
  skipDbMutation?: boolean;
  onSubmitSuccess?: () => void;
  // bulk-update-yield and bulk-update-crop-dates report back which crops they silently skipped (already had a
  // value, not flagged for Soil+3.0 data collection) instead of just returning success — lets the caller keep
  // those specific rows selected so the user can see and revisit exactly what didn't update
  onCropsSkipped?: (skippedCropIds: string[]) => void;
};

// Only the request(s) relevant to the practice type this drawer instance is currently editing — comments are
// submitted for whichever ones actually have a comment left, same rule as AddEditCropDrawer.tsx.
const getRelevantPendingEditingRequestIds = (
  practiceType: MissingDataDrawerPracticeType,
  {
    pendingCropVerificationRequest,
    pendingSeedingVerificationRequest,
    pendingTillageVerificationRequests,
    pendingFertilizerVerificationRequests,
  }: {
    pendingCropVerificationRequest: VerificationEditingRequest | undefined;
    pendingSeedingVerificationRequest: VerificationEditingRequest | undefined;
    pendingTillageVerificationRequests: VerificationEditingRequest[];
    pendingFertilizerVerificationRequests: VerificationEditingRequest[];
  }
): string[] => {
  switch (practiceType) {
    case PRACTICE_SLUG.CROPS:
    case YIELD_PSEUDO_PRACTICE_TYPE:
      return pendingCropVerificationRequest ? [pendingCropVerificationRequest.id] : [];
    case PRACTICE_SLUG.SEEDINGS:
      return pendingSeedingVerificationRequest ? [pendingSeedingVerificationRequest.id] : [];
    case PRACTICE_SLUG.TILLAGES:
      return pendingTillageVerificationRequests.map((request) => request.id);
    case PRACTICE_SLUG.ORGANIC_FERTILIZERS:
    case PRACTICE_SLUG.MINERAL_FERTILIZERS:
      return pendingFertilizerVerificationRequests.map((request) => request.id);
    default:
      return [];
  }
};

export const AddEditMissingDataDrawer = ({
  showDrawer,
  missingDataFormItems,
  onClose,
  practiceType,
  selectedYear,
  shouldValidateOnMount = false,
  onDatesCollected,
  skipDbMutation = false,
  onSubmitSuccess,
  onCropsSkipped,
}: AddEditMissingSeedingDrawerProps) => {
  const { t } = useTranslation(["allFieldView", "historicalPracticeDataTableView"]);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialValidationDone, setInitialValidationDone] = useState(false);
  const [undersowingCropsBrokenByChange, setUndersowingCropsBrokenByChange] = useState<string[]>([]);
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  const [recommendedSeedingDate, setRecommendedSeedingDate] = useState<Moment>(moment());
  const [recommendedHarvestDate, setRecommendedHarvestDate] = useState<Moment>(moment());

  const fieldIds = missingDataFormItems.map((item) => item.fieldId);
  const affectedFieldIds = missingDataFormItems.map((item) => item.fieldId);
  const selectedCrops = useMemo(() => missingDataFormItems.map((item) => item.crop).filter((crop) => crop !== undefined), [missingDataFormItems]) as CropFCPO[];

  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(skipDbMutation ? undefined : currentFarm);
  const fieldIdsWithSelectedCrops = useMemo(() => Array.from(new Set(missingDataFormItems.map((item) => item.fieldId))), [missingDataFormItems]);

  // Editing requests are only surfaced for a single selected item — a bulk selection has no single row to attach
  // a banner or "add a new entry" unlock to, so this stays out of the bulk-edit flow entirely.
  const singleCrop = missingDataFormItems.length === 1 ? missingDataFormItems[0].crop : undefined;

  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isSubmissionFeatureEnabled, submissionStatusValue } = useSubmissionStatus(activeFarmReportingYear);
  const { data: editingRequests } = useGetVerificationEditingRequests({ farmId: currentFarm?.id ?? "" });
  const { mutateAsync: submitEditingRequestComment } = useUpdateVerificationEditingRequest({ farmId: currentFarm?.id ?? "" });

  const pendingCropVerificationRequest = singleCrop
    ? editingRequests?.find((r) => r.requestType === EDITING_REQUEST_TYPE.CROP && r.completedAt === null && r.fieldCropId === singleCrop.id)
    : undefined;

  const pendingSeedingVerificationRequest =
    singleCrop && singleCrop.seedingPracticeId
      ? editingRequests?.find(
          (r) => r.requestType === EDITING_REQUEST_TYPE.SEEDING && r.completedAt === null && r.fieldSeedingId === singleCrop.seedingPracticeId
        )
      : undefined;

  const pendingTillageVerificationRequests =
    missingDataFormItems.length === 1
      ? editingRequests?.filter(
          (r) =>
            r.requestType === EDITING_REQUEST_TYPE.TILLAGE &&
            r.completedAt === null &&
            (missingDataFormItems[0].tillages ?? []).some((tillage) => tillage.id === r.fieldTillageId)
        ) ?? []
      : [];

  const pendingFertilizerVerificationRequests =
    missingDataFormItems.length === 1
      ? editingRequests?.filter(
          (r) =>
            (r.requestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER || r.requestType === EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER) &&
            r.completedAt === null &&
            [...(missingDataFormItems[0].organicFertilizers ?? []), ...(missingDataFormItems[0].mineralFertilizers ?? [])].some(
              (fertilizer) => fertilizer.id === r.fieldMineralFertilizerId || fertilizer.id === r.fieldOrganicFertilizerId
            )
        ) ?? []
      : [];

  const relevantPendingEditingRequestIds = getRelevantPendingEditingRequestIds(practiceType, {
    pendingCropVerificationRequest,
    pendingSeedingVerificationRequest,
    pendingTillageVerificationRequests,
    pendingFertilizerVerificationRequests,
  });

  const submitPendingEditingRequestComments = async () => {
    if (!relevantPendingEditingRequestIds.length) return;

    const formValues = form.getFieldsValue(true);
    const idsWithComments = relevantPendingEditingRequestIds.filter((requestId) => !!formValues.editingRequestComment?.[requestId]);

    if (idsWithComments.length) {
      await Promise.all(
        idsWithComments.map((requestId) => submitEditingRequestComment({ requestId, userComment: formValues.editingRequestComment[requestId] as string }))
      );
    }
  };
  // the growing period form seeds itself from this, so it may not be rebuilt on every render
  const selectedCropsWithFieldIds = useMemo(
    () =>
      missingDataFormItems
        .filter((item): item is MissingDataForm & { crop: CropFCPO } => item.crop != null)
        .map((item) => ({ crop: item.crop, fieldId: item.fieldId })),
    [missingDataFormItems]
  );

  const existingCropsForValidationByField = useMemo(() => {
    if (!allFieldsPractices) return {};
    const editedCropIds = new Set(missingDataFormItems.map((item) => item.key));
    const result: Record<string, CropFCPO[]> = {};
    for (const fieldId of fieldIdsWithSelectedCrops) {
      const fieldCrops = allFieldsPractices[fieldId]?.crops ?? [];
      result[fieldId] = fieldCrops.filter((crop) => crop.year === selectedYear && !editedCropIds.has(crop.id));
    }
    return result;
  }, [allFieldsPractices, missingDataFormItems, selectedYear, fieldIdsWithSelectedCrops]);

  const { mutateAsync: updateSeeding } = useBulkUpdateCropSeedingPractice({ fieldIds: fieldIds });
  const { mutateAsync: updateOrganic } = useBulkUpdateOrganicFertilizerPractice({ fieldIds: fieldIds });
  const { mutateAsync: updateMineral } = useBulkUpdateMineralFertilizerPractice({ fieldIds: fieldIds });
  const { mutateAsync: updateTillage } = useBulkUpdateTillagePractice({ fieldIds: fieldIds });
  const { mutateAsync: updateCropDates } = useBulkUpdateCropDates({ fieldIds: fieldIds });
  const { mutateAsync: updateCropYield } = useBulkUpdateCropYield({ fieldIds: fieldIds });
  // no bulk endpoint for mineral fertilizer yet — reuses the single-crop update API instead, so this drawer only
  // ever edits one crop's mineral fertilizer at a time
  const { mutateAsync: updateFieldCropPractice } = useUpdateFieldCropPractice({ fieldId: missingDataFormItems[0].fieldId, farmId: currentFarm?.id });
  const { data: fieldDetails } = useGetFieldDetails({ id: missingDataFormItems[0].fieldId }); // We can select the first one since the fieldDetails will only be used in the MissingDataDrawerHeader if there is only one field selected
  const { data: practiceCrops } = useGetPracticeCrops({ enabled: practiceType === YIELD_PSEUDO_PRACTICE_TYPE || practiceType === PRACTICE_SLUG.SEEDINGS });

  // Growing Period
  const isGrowingPeriodDisabled = missingDataFormItems.some((item) => item.isGrowingPeriodEditable === false);

  // Seeding
  const seedingTypes = missingDataFormItems.flatMap((item) => item.seedings ?? []);
  const isSeedingDisabled = missingDataFormItems.some((item) => item.isSeedingEditable === false);
  const areNoSeedingCropsSelected =
    selectedCrops.length > 0 && selectedCrops.every((crop) => practiceCrops?.find((practiceCrop) => practiceCrop.id === crop.cropId)?.isPerennial);

  // Tillage
  const selectedTillages = missingDataFormItems.flatMap((missingItem) => missingItem.tillages).filter((missingItem) => missingItem !== null);
  const tillagesBulkShow = getPracticeConfigForBulk(selectedTillages, PRACTICE_SLUG.TILLAGES, submissionStatusValue?.lastSubmissionYear);
  const practiceSignature = (practices: PracticeFCPO[] | null): string =>
    (practices ?? [])
      .map((p) => p.assignedPracticeId)
      .sort()
      .join("|");

  const confirmedTillageItems = missingDataFormItems.filter((item) => !item.isTillageEditable);
  const tillageDisabled =
    confirmedTillageItems.length > 0 &&
    !confirmedTillageItems.every((item) => practiceSignature(item.tillages) === practiceSignature(confirmedTillageItems[0].tillages));
  // Organic Fertilizer
  const selectedOrganicFertilizers = missingDataFormItems
    .flatMap((missingItem) => missingItem.organicFertilizers)
    .filter((missingItem) => missingItem !== null);
  const organicBulkShow = getPracticeConfigForBulk(selectedOrganicFertilizers, PRACTICE_SLUG.ORGANIC_FERTILIZERS, submissionStatusValue?.lastSubmissionYear);
  const confirmedOrganicItems = missingDataFormItems.filter((item) => !item.isOrganicEditable);
  const organicDisabled =
    confirmedOrganicItems.length > 0 &&
    !confirmedOrganicItems.every((item) => practiceSignature(item.organicFertilizers) === practiceSignature(confirmedOrganicItems[0].organicFertilizers));

  // Mineral Fertilizer — no bulk edit support yet, always editable via the single-crop drawer. The
  // activation-toggle bundle IS real though, mirroring AddEditCropDrawer.tsx's handleBundleActiveStateChange
  const selectedMineralFertilizers = missingDataFormItems
    .flatMap((missingItem) => missingItem.mineralFertilizers ?? [])
    .filter((missingItem) => missingItem !== null);
  const mineralBulkShow = getPracticeConfigForBulk(selectedMineralFertilizers, PRACTICE_SLUG.MINERAL_FERTILIZERS, submissionStatusValue?.lastSubmissionYear);
  const confirmedMineralItems = missingDataFormItems.filter((item) => item.isMineralEditable === false);
  const mineralDisabled =
    confirmedMineralItems.length > 0 &&
    !confirmedMineralItems.every(
      (item) => practiceSignature(item.mineralFertilizers ?? null) === practiceSignature(confirmedMineralItems[0].mineralFertilizers ?? null)
    );

  const isTillageDepthRequired = missingDataFormItems.some((item) => {
    if (!item.crop) return true;
    const availablePracticeBundles = allFieldsPractices?.[item.fieldId]?.availablePracticeBundles ?? [];
    const carbonProgram = currentFarm?.carbonProgram ?? null;
    return isRequired(
      getInputRequirementConfig(carbonProgram, availablePracticeBundles, item.crop, isFieldEnrolled(availablePracticeBundles, carbonProgram))?.tillageDepth
    );
  });

  const mineralFertilizerFieldId = missingDataFormItems[0].fieldId;
  // every selected field must be enrolled in the program before any of them can opt into mineral fertilizer
  const everyFieldIsEnrolled = fieldIdsWithSelectedCrops.every((fieldId) =>
    isFieldEnrolled(allFieldsPractices?.[fieldId]?.availablePracticeBundles ?? [], currentFarm?.carbonProgram ?? null)
  );
  const [mineralPracticeBundles, setMineralPracticeBundles] = useState<PracticeBundle[]>(
    () => allFieldsPractices?.[mineralFertilizerFieldId]?.availablePracticeBundles ?? []
  );
  const [mineralOptedInOverride, setMineralOptedInOverride] = useState<boolean | null>(null);

  const mineralPracticeBundle = useMemo<PracticeBundle | undefined>(() => {
    const firstFieldBundle = mineralPracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS);
    if (!firstFieldBundle || fieldIdsWithSelectedCrops.length === 1) return firstFieldBundle;

    const everyFieldOptedIn = fieldIdsWithSelectedCrops.every((fieldId) =>
      (allFieldsPractices?.[fieldId]?.availablePracticeBundles ?? []).some((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS && bundle.active)
    );
    return { ...firstFieldBundle, active: mineralOptedInOverride ?? everyFieldOptedIn };
  }, [mineralPracticeBundles, mineralOptedInOverride, fieldIdsWithSelectedCrops, allFieldsPractices]);

  const { mutate: addMineralFieldPracticeParticipations } = useAddFieldPracticeParticipations(fieldIdsWithSelectedCrops, currentFarm?.id);
  const { mutate: deleteMineralFieldPracticeParticipations } = useDeleteFieldPracticeParticipations(fieldIdsWithSelectedCrops, currentFarm?.id);

  // The opt-in is per field but the toggle speaks for the whole selection, so it fans out over every selected field
  const handleMineralBundleActivation = async (bundleSlug: PracticeSlug, active: boolean) => {
    setMineralOptedInOverride(active);
    if (active) {
      addMineralFieldPracticeParticipations(fieldIdsWithSelectedCrops.map((fieldId) => ({ fieldId, practiceSlugs: [bundleSlug] })));
    } else {
      fieldIdsWithSelectedCrops.forEach((fieldId) => deleteMineralFieldPracticeParticipations({ fieldId, practiceSlugs: [bundleSlug] }));
    }

    setMineralPracticeBundles((prev) => {
      const existingBundle = prev.find((bundle) => bundle.slug === bundleSlug);
      const updatedBundle: PracticeBundle = {
        hasConfirmedData: false,
        startYear: null,
        discontinuedYear: null,
        reactivatedYear: null,
        enrollable: true,
        ...existingBundle,
        active,
        slug: bundleSlug,
      };
      return [...prev.filter((bundle) => bundle.slug !== bundleSlug), updatedBundle];
    });
  };

  // Yield
  const cropPMSQuery = usePracticeManagementCropParameter(
    selectedCrops?.[0]?.cropId,
    currentFarm?.carbonProgram?.country.toLowerCase() as PracticeManagement.CountryCode
  );
  const isYieldDisabled = missingDataFormItems.some((item) => item.isYieldEditable === false);
  const cropForYield = missingDataFormItems[0]?.crop;
  const cropDetailsForYield = practiceCrops?.find((practiceCrop) => practiceCrop.id === cropForYield?.cropId);
  const isZeroYieldsAllowed = cropDetailsForYield?.harvestIsMandatory === false;
  const yieldValues = missingDataFormItems.map((item) => item.yieldPerHa ?? null);
  const initialYieldValue = yieldValues.every((value) => value === yieldValues[0]) ? yieldValues[0] : null;
  const yieldUnitValues = missingDataFormItems.map((item) => item.yieldPerHaUnit ?? null);
  const initialYieldUnitValue = yieldUnitValues.every((value) => value === yieldUnitValues[0]) ? yieldUnitValues[0] : null;
  const yieldSanityNotes = missingDataFormItems.map((item) => item.crop?.sanityNoteYield ?? null);
  const initialYieldSanityNote = yieldSanityNotes.every((value) => value === yieldSanityNotes[0]) ? yieldSanityNotes[0] : null;
  const [committedYield, setCommittedYield] = useState<number | null>(initialYieldValue);
  const [committedYieldUnit, setCommittedYieldUnit] = useState<HarvestUnitValue | null>(initialYieldUnitValue);

  const isBulkModeEnabled =
    (practiceType === PRACTICE_SLUG.CROPS && !isGrowingPeriodDisabled) ||
    (practiceType === PRACTICE_SLUG.SEEDINGS && !isSeedingDisabled) ||
    (practiceType === PRACTICE_SLUG.TILLAGES && !tillageDisabled) ||
    (practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS && !organicDisabled) ||
    (practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS && !mineralDisabled) ||
    (practiceType === YIELD_PSEUDO_PRACTICE_TYPE && !isYieldDisabled);

  const onDrawerClose = () => {
    onClose();
  };

  const onSubmit = async (skipWarning?: boolean) => {
    // We can select the first one since it is only needed for to get the organicFertilizers and tillages over the createCropPracticeRequestBody() function. We do not care about the crops here.
    const { crop } = missingDataFormItems[0];
    if (!crop) return;
    setIsLoading(true);
    const formValues = form.getFieldsValue(true);
    const { isTillageApplied, isMineralApplied, isMineralActivated, isOrganicApplied, hasFertilizerManagement, ...formPracticeObject } = {
      ...formValues,
    };

    try {
      // Crop growing period (dates)
      if (practiceType === PRACTICE_SLUG.CROPS) {
        const seedingDateRange = form.getFieldValue("seedingDateRange") as [moment.Moment, moment.Moment] | undefined;
        const harvestDateRange = form.getFieldValue("harvestDateRange") as [moment.Moment, moment.Moment] | undefined;
        if (!seedingDateRange?.[0] || !seedingDateRange?.[1] || !harvestDateRange?.[0] || !harvestDateRange?.[1]) {
          setIsLoading(false);
          return;
        }
        const editableItems = missingDataFormItems.filter((item) => item.isGrowingPeriodEditable);
        const farmFieldCropIds = editableItems.map((item) => item.key);

        // mirrors the single-crop/bulk-edit drawers' own check (CropValidationUtils.ts) — applying the same new
        // dates to every selected crop here can just as easily orphan an existing Undersowing crop's overlap
        if (!skipWarning) {
          const changes = editableItems
            .filter((item): item is MissingDataForm & { crop: CropFCPO } => !!item.crop)
            .map((item) => ({
              fieldId: item.fieldId,
              crop: { ...item.crop, seedingStartDate: seedingDateRange[0].toISOString(), harvestEndDate: harvestDateRange[1].toISOString() },
            }));
          const undersowingWarnings = getUndersowingWarningsForChanges(
            changes,
            (fieldId) => allFieldsPractices?.[fieldId]?.crops ?? [],
            currentFarm?.carbonProgram
          );

          if (undersowingWarnings.length > 0) {
            setUndersowingCropsBrokenByChange(undersowingWarnings);
            setIsLoading(false);
            return;
          }
        }

        if (onDatesCollected) {
          farmFieldCropIds.forEach((key) => {
            onDatesCollected(
              key,
              seedingDateRange[0].toISOString(),
              seedingDateRange[1].toISOString(),
              harvestDateRange[0].toISOString(),
              harvestDateRange[1].toISOString()
            );
          });
        }

        if (!skipDbMutation) {
          const request = farmFieldCropIds.map((fieldCropId) => ({
            fieldCropId,
            seedingStart: seedingDateRange[0].toISOString(),
            seedingEnd: seedingDateRange[1].toISOString(),
            harvestStart: harvestDateRange[0].toISOString(),
            harvestEnd: harvestDateRange[1].toISOString(),
          }));
          const response = await updateCropDates(request);
          if (response.success) {
            // must run before onCropsSkipped, or a caller's own onSubmitSuccess (e.g. clearing selection) would overwrite it
            onSubmitSuccess?.();
            if (response.skippedCropIds.length > 0) {
              message({
                content: t("historicalPracticeDataTableView:drawer.skippedCropsWarning", {
                  updatedCount: farmFieldCropIds.length - response.skippedCropIds.length,
                  total: farmFieldCropIds.length,
                }),
              });
              onCropsSkipped?.(response.skippedCropIds);
            }
            await submitPendingEditingRequestComments();
            onClose();
          }
        } else {
          onSubmitSuccess?.();
          onClose();
        }
        setIsLoading(false);
        return;
      }

      // Yield
      if (practiceType === YIELD_PSEUDO_PRACTICE_TYPE) {
        const yieldPerHaValue = form.getFieldValue([CROP, "yieldPerHa"]) as number | null;
        const yieldPerHaUnitValue = (form.getFieldValue([CROP, "yieldPerHaUnit"]) as HarvestUnitValue | undefined) ?? HARVEST_UNIT_VALUE.tonnesPerHa;
        const sanityNoteYieldValue = form.getFieldValue([CROP, "sanityNoteYield"]) as string | null;
        const farmFieldCropIds = missingDataFormItems.filter((item) => item.isYieldEditable !== false).map((item) => item.key);

        if (!skipDbMutation) {
          const request: BulkUpdateCropYieldRequestBody[] = farmFieldCropIds.map((farmFieldCropId) => ({
            farmFieldCropId,
            yieldPerHa: yieldPerHaValue,
            yieldPerHaUnit: yieldPerHaUnitValue,
            sanityNoteYield: sanityNoteYieldValue,
          }));
          const response = await updateCropYield(request);
          if (response.success) {
            // must run before onCropsSkipped, or a caller's own onSubmitSuccess (e.g. clearing selection) would overwrite it
            onSubmitSuccess?.();
            if (response.skippedCropIds.length > 0) {
              message({
                content: t("historicalPracticeDataTableView:drawer.skippedCropsWarning", {
                  updatedCount: farmFieldCropIds.length - response.skippedCropIds.length,
                  total: farmFieldCropIds.length,
                }),
              });
              onCropsSkipped?.(response.skippedCropIds);
            }
            await submitPendingEditingRequestComments();
            onClose();
          }
        } else {
          onSubmitSuccess?.();
          onClose();
        }
        setIsLoading(false);
        return;
      }

      // Seeding
      if (practiceType === PRACTICE_SLUG.SEEDINGS) {
        const seedingPracticeTypeValue = form.getFieldValue("seedingPracticeType");
        // This can be null if the user has not selected a seeding type, or they are multiple different ones since we have the bulk mode enabled
        // In both cases we do not want to trigger a call to tge BE since nothing has been changed
        if (!seedingPracticeTypeValue) {
          onClose();
          return;
        }

        const farmFieldCropIds = missingDataFormItems.filter((item) => item.isSeedingEditable).map((missingItem) => missingItem.key);
        if (!skipDbMutation) {
          const request: BulkUpdateSeedingPracticesRequestBody = {
            farmFieldCropIds: farmFieldCropIds,
            seedingPracticeType: seedingPracticeTypeValue,
          };
          const response = await updateSeeding(request);
          if (response.success) {
            await submitPendingEditingRequestComments();
            onClose();
          }
        } else {
          onClose();
        }
      }

      const formPractices = Object.entries(formPracticeObject as FormPracticeObject).map(([_, practiceData]) => {
        const { dateRange, ...rest } = practiceData;

        return {
          ...rest,
          startDate: (dateRange as moment.Moment[])?.[0]?.toISOString(),
          endDate: (dateRange as moment.Moment[])?.[1]?.toISOString(),
        } as PracticeFCPO;
      });

      const existingFertilizers = organicBulkShow?.practiceToShow ?? [];
      const existingMineralFertilizers = mineralBulkShow?.practiceToShow ?? [];
      const existingTillages = tillagesBulkShow?.practiceToShow ?? [];

      const request = createCropPracticeRequestBody(
        crop,
        formPractices,
        isTillageApplied,
        isMineralApplied,
        isMineralActivated,
        isOrganicApplied,
        hasFertilizerManagement,
        [...existingFertilizers, ...existingMineralFertilizers, ...existingTillages],
        isSoilPlus3DataGapFeatureActive
      );

      const organicFertilizers = request.practices.filter((practice) => practice.type === "organicFertilizers");
      const mineralFertilizers = request.practices.filter((practice) => practice.type === "mineralFertilizers");
      const tillages = request.practices.filter((practice) => practice.type === "tillages");

      if (practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
        if (!skipDbMutation) {
          const farmFieldCropIds = missingDataFormItems
            .filter((item) => item.isOrganicEditable || item.areOrganicDatesEditable)
            .map((missingItem) => missingItem.key);
          const request: BulkUpdateOrganicFertilizerPracticesRequestBody = {
            farmFieldCropIds: farmFieldCropIds,
            practices: organicFertilizers,
          };
          const response = await updateOrganic(request);
          if (response.success) {
            await submitPendingEditingRequestComments();
            onClose();
          }
        } else {
          onClose();
        }
      }
      if (practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
        if (!skipDbMutation) {
          const farmFieldCropIds = missingDataFormItems.filter((item) => item.isMineralEditable !== false).map((missingItem) => missingItem.key);
          const request: BulkUpdateMineralFertilizerPracticesRequestBody = {
            farmFieldCropIds: farmFieldCropIds,
            practices: mineralFertilizers,
          };
          const response = await updateMineral(request);
          if (response.success) {
            onClose();
          }
        } else {
          onClose();
        }
      }
      if (practiceType === PRACTICE_SLUG.TILLAGES) {
        if (!skipDbMutation) {
          const farmFieldCropIds = missingDataFormItems
            .filter((item) => item.isTillageEditable || item.isTillageDepthEditable || item.areTillageDatesEditable)
            .map((foo) => foo.key);
          const request: BulkUpdateTillagePracticesRequestBody = {
            farmFieldCropIds: farmFieldCropIds,
            practices: tillages,
          };
          const response = await updateTillage(request);
          if (response.success) {
            await submitPendingEditingRequestComments();
            onClose();
          }
        } else {
          onClose();
        }
      }
      // no bulk mineral fertilizer endpoint exists yet, so this goes through the same single-crop update API
      // Timeline's AddEditCropDrawer uses, instead of one of the useBulkUpdate*Practice hooks above. That endpoint
      // replaces the crop's ENTIRE practices list — Timeline is safe because its form always has every practice
      // type's real applied/not-applied answer, so createCropPracticeRequestBody includes all of them. This drawer
      // only ever renders the mineral fertilizer inputs, so isTillageApplied/isOrganicApplied above are undefined
      // and those types are silently left out of `request.practices` — carry the crop's existing tillage/organic
      // practices forward unchanged so this save doesn't wipe them out
      if (practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
        if (!skipDbMutation) {
          const mineralOnlyRequest = {
            ...request,
            practices: [...request.practices, ...(missingDataFormItems[0].tillages ?? []), ...(missingDataFormItems[0].organicFertilizers ?? [])],
          };
          await updateFieldCropPractice({ requestBody: mineralOnlyRequest, cropId: crop.id });
          await submitPendingEditingRequestComments();
        }
        onClose();
      }
    } catch (e) {
      if (e) {
        const errorMessage = e instanceof AxiosError ? getUserFacingApiErrorMessage(e) : e instanceof Error ? e.message : String(e);
        message({ content: `${errorMessage}`, icon: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const incomplete = () => {
    message({ content: t("allFieldView:errors.required"), icon: "error" });
  };

  useEffect(() => {
    if (!showDrawer) {
      setInitialValidationDone(false);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (!showDrawer || !shouldValidateOnMount || initialValidationDone) return;

    let isCancelled = false;
    const timeoutId = setTimeout(() => {
      form
        .validateFields()
        .then(() => {
          if (!isCancelled) setInitialValidationDone(true);
        })
        .catch(() => {
          if (!isCancelled) setInitialValidationDone(true);
        });
    }, VALIDATION_DELAY_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [showDrawer, shouldValidateOnMount, initialValidationDone, form]);

  useEffect(() => {
    const { seedingDate, harvestDate } = getDefaultBulkDataEntryDateValues(selectedCrops);
    const yearFallback = moment().year(selectedYear);
    setRecommendedSeedingDate(seedingDate.isValid() ? seedingDate : yearFallback);
    setRecommendedHarvestDate(harvestDate.isValid() ? harvestDate : yearFallback);
  }, [selectedCrops, selectedYear]);

  return (
    <Drawer
      className="add-edit-missing-data-drawer"
      dataTestId="add-edit-missing-data-drawer"
      title={
        practiceType === PRACTICE_SLUG.CROPS
          ? t("historicalPracticeDataTableView:drawer.header.growingPeriod")
          : t(`historicalPracticeDataTableView:drawer.header.${practiceType}`)
      }
      visible={showDrawer}
      onClose={onDrawerClose}
      destroyOnHidden={true}
    >
      <MissingDataDrawerHeader year={selectedYear} fieldDetails={fieldDetails} fieldIds={affectedFieldIds} crops={selectedCrops} />
      {missingDataFormItems.length > 1 && !isBulkModeEnabled ? (
        <AlertBanner
          title={t("historicalPracticeDataTableView:drawer.bulkUnavailableBanner.title")}
          description={t("historicalPracticeDataTableView:drawer.bulkUnavailableBanner.description")}
          type="info-highlighted"
          iconName="tabler/info-circle"
          iconAlign="start"
          headingSize="h6"
          descriptionSize="paragraph-m"
          isTitleBold={true}
          gap={4}
          className="margin-top-200 margin-bottom-200"
        />
      ) : (
        <Form
          form={form}
          name="cropForm"
          onFinish={() => onSubmit()}
          onFinishFailed={incomplete}
          preserve={true} // from my research, this helps to prevent re-validation from clearing touched state
        >
          {practiceType === PRACTICE_SLUG.CROPS && (
            <>
              {isSubmissionFeatureEnabled && !getEnvironment().admin && pendingCropVerificationRequest && singleCrop && (
                <EditingRequestBanner
                  key={EDITING_REQUEST_TYPE.CROP}
                  type={EDITING_REQUEST_TYPE.CROP}
                  farmId={currentFarm!.id}
                  id={singleCrop.id}
                  requestId={pendingCropVerificationRequest.id}
                />
              )}
              <MissingCropGrowingPeriod
                recommendedSeedingDate={recommendedSeedingDate}
                recommendedHarvestDate={recommendedHarvestDate}
                existingCropsForValidationByField={existingCropsForValidationByField}
                selectedCropsWithFieldIds={selectedCropsWithFieldIds}
                disabled={isGrowingPeriodDisabled}
              />
            </>
          )}
          {practiceType === PRACTICE_SLUG.SEEDINGS && (
            <>
              {isSubmissionFeatureEnabled && !getEnvironment().admin && pendingSeedingVerificationRequest && singleCrop?.seedingPracticeId && (
                <EditingRequestBanner
                  key={EDITING_REQUEST_TYPE.SEEDING}
                  type={EDITING_REQUEST_TYPE.SEEDING}
                  farmId={currentFarm!.id}
                  id={singleCrop.seedingPracticeId}
                  requestId={pendingSeedingVerificationRequest.id}
                />
              )}
              <MissingSeedingForm seedingTypes={seedingTypes} disabled={isSeedingDisabled} allowNone={areNoSeedingCropsSelected} />
            </>
          )}
          {practiceType === PRACTICE_SLUG.TILLAGES && (
            <AddEditTillageForm
              baselineYears={undefined}
              bulkEditMode={missingDataFormItems.length > 1}
              cropId={uuid()} // We only need the cropId to create a TILLAGE_BLANK_PRACTICE, the cropId of the TILLAGE_BLANK_PRACTICE is not getting used for anything else
              disabled={tillageDisabled} // This is needed as prop but will be ignored since we are providing 'practiceConfigForBulk'
              existingTillages={tillagesBulkShow?.practiceToShow ?? []}
              optionalInputsAreShown={false}
              practiceConfigForBulk={tillagesBulkShow ? { ...tillagesBulkShow, toggleDisabled: tillagesBulkShow.toggleDisabled ?? tillageDisabled } : undefined}
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              requirePreciseTillageDepth={!!currentFarm?.carbonProgram?.fieldsManagement.requirePreciseTillageDepth}
              requireTillageSoilMixing={!!currentFarm?.carbonProgram?.fieldsManagement.requireTillageSoilMixing}
              requirementConfig={{ ...(isTillageDepthRequired ? { tillageDepth: "requiredForSaving" } : {}), dates: "requiredForSaving" }}
              year={selectedYear}
              editingRequests={pendingTillageVerificationRequests}
              isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
              farmId={currentFarm?.id}
            />
          )}
          {practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS && (
            <AddEditOrganicAndMineralFertilizers
              chosenFertilizerType="organic"
              disabled={organicDisabled} // This is needed as prop but will be ignored since we are providing 'practiceConfigForBulk'
              existingFertilizers={organicBulkShow?.practiceToShow ?? []}
              baselineYears={undefined}
              year={selectedYear}
              practiceBundle={FakeOrganicFertilizerPracticeBundle}
              practiceConfigForBulk={organicBulkShow ? { ...organicBulkShow, toggleDisabled: organicBulkShow.toggleDisabled ?? organicDisabled } : undefined}
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              requirementConfig={{ dates: "requiredForSaving" }}
              bulkEditMode={missingDataFormItems.length > 1}
              editingRequests={pendingFertilizerVerificationRequests}
              isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
              farmId={currentFarm?.id}
            />
          )}
          {practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS && (
            <AddEditOrganicAndMineralFertilizers
              chosenFertilizerType="mineral"
              disabled={mineralDisabled} // This is needed as prop but will be ignored since we are providing 'practiceConfigForBulk'
              existingFertilizers={mineralBulkShow?.practiceToShow ?? []}
              baselineYears={undefined}
              year={selectedYear}
              practiceBundle={mineralPracticeBundle}
              onBundleActivation={handleMineralBundleActivation}
              fieldIsEnrolled={everyFieldIsEnrolled}
              practiceConfigForBulk={mineralBulkShow ? { ...mineralBulkShow, toggleDisabled: mineralBulkShow.toggleDisabled ?? mineralDisabled } : undefined}
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              requirementConfig={{ dates: "requiredForSaving" }}
              bulkEditMode={missingDataFormItems.length > 1}
              editingRequests={pendingFertilizerVerificationRequests}
              isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
              farmId={currentFarm?.id}
            />
          )}
          {practiceType === YIELD_PSEUDO_PRACTICE_TYPE && (
            <div className="add-edit-crop-form-container">
              {isSubmissionFeatureEnabled && !getEnvironment().admin && pendingCropVerificationRequest && singleCrop && (
                <EditingRequestBanner
                  key={EDITING_REQUEST_TYPE.CROP}
                  type={EDITING_REQUEST_TYPE.CROP}
                  farmId={currentFarm!.id}
                  id={singleCrop.id}
                  requestId={pendingCropVerificationRequest.id}
                />
              )}
              <CropYieldSection
                label={t("allFieldView:cropForm.yield")}
                initialValue={initialYieldValue}
                initialValueUnit={initialYieldUnitValue}
                initialNoteYield={initialYieldSanityNote}
                initialIsHarvested={cropForYield?.wasHarvested ?? !isZeroYieldsAllowed}
                disabled={isYieldDisabled}
                isRequired={true}
                isZeroYieldsAllowed={isZeroYieldsAllowed}
                committedYield={committedYield}
                committedYieldUnit={committedYieldUnit}
                cropName={cropDetailsForYield?.name}
                yieldBenchmarkBoundsTperHa={cropPMSQuery.data?.harvestTonsPerHectare}
                onChange={(value, unit) => {
                  setCommittedYield(value);
                  setCommittedYieldUnit((unit as HarvestUnitValue | null) ?? null);
                }}
              />
            </div>
          )}
          <StickyFooterButtons absolute>
            <div className="row-space-between">
              <Button dataTestId="save-crop-drawer" loading={isLoading} type="primary" htmlType="submit">
                {t("historicalPracticeDataTableView:drawer.save")}
              </Button>
            </div>
          </StickyFooterButtons>
        </Form>
      )}
      <UndersowingOverlapWarningModal
        dataTestId="missing-data-crop-form-undersowing-overlap-warning"
        cropNames={undersowingCropsBrokenByChange}
        onCancel={() => setUndersowingCropsBrokenByChange([])}
        onConfirm={() => {
          setUndersowingCropsBrokenByChange([]);
          onSubmit(true);
        }}
      />
    </Drawer>
  );
};
